/**
 * A signed-in session that did not come from GitHub, so automated end-to-end tests can
 * drive a real application (owner request, 2026-08-25).
 *
 * This is the PWA half of the Server's test sign-in and is a back door in exactly the
 * same way; SEC-6 removes both in M14. Three things keep it small:
 *
 *   - Nothing here mints anything. The token comes from the Server, which only issues
 *     one when a deployment has deliberately configured the secret. Putting a made-up
 *     token in this key gets you a 401 on the first call, not a session.
 *   - It never touches the Supabase client, so the ordinary sign-in path is unchanged
 *     and cannot be affected by anything in this file.
 *   - While one is active the app says so on screen, so nobody is ever signed in this
 *     way without knowing.
 *
 * To use one, put the Server's response in local storage and reload:
 *
 *   localStorage.setItem("factorium.testSession", JSON.stringify({ accessToken, expiresAt }))
 */

export const TEST_SESSION_KEY = "factorium.testSession";

export interface TestSession {
  accessToken: string;
  /** ISO timestamp from the Server. An expired session is treated as absent. */
  expiresAt: string;
}

export function readTestSession(now: number = Date.now()): TestSession | null {
  let raw: string | null;

  try {
    raw = window.localStorage.getItem(TEST_SESSION_KEY);
  } catch {
    // Private-mode browsers can refuse local storage outright.
    return null;
  }

  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<TestSession>;
    if (typeof parsed.accessToken !== "string" || typeof parsed.expiresAt !== "string") return null;

    const expires = new Date(parsed.expiresAt).getTime();
    if (Number.isNaN(expires) || expires <= now) {
      // Tidy up rather than leaving a dead session to fail every call with a 401 that
      // looks like something else.
      clearTestSession();
      return null;
    }

    return { accessToken: parsed.accessToken, expiresAt: parsed.expiresAt };
  } catch {
    return null;
  }
}

export function clearTestSession(): void {
  try {
    window.localStorage.removeItem(TEST_SESSION_KEY);
  } catch {
    // Nothing to do; the session expires on its own within the hour either way.
  }
}
